import { useForm } from "react-hook-form";
import { useState, useEffect, useRef, useMemo } from "react";
import Modal from "./Modal";
import { useDispatch, useSelector } from "react-redux";
import {
  obtenerTiposPlantillas,
  obtenerRolesPlantillas,
  actualizarTipoPlantilla,
} from "../../redux/plantillasTiposSlice";
import WindowDimension from "../../Hooks/WindowDimension";
import Multiselect from "./Multiselect";
import Required from "../../Components/Required";
import { OrderTable } from "../../Components/OrderTable";
import useSubmit from "../../Hooks/useSubmit";
import { getAdminTemplateTypePage } from "../../Api/content";
import { useLocale } from "../../i18n/localeContext";
import { Skeleton } from "antd";
import { _translateValue } from "../translation-gestor/translateValue";

import { useValidation } from "../../Hooks/useValidation";

const SCREEN_NAME = "PantallaTiposPlantillas";
function TiposPlantillas(props) {
  const { validation } = useValidation();
  const { locale } = useLocale();
  const [adminTemplateTypePage, setAdminTemplateTypePage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [translatedNombres, setTranslatedNombres] = useState({});
  const [translatedDestinatarios, setTranslatedDestinatarios] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [modalState, changeModalState] = useState(false);
  const [registerSelected = {}, selectReg] = useState();
  const multiSelectRef = useRef(null);
  const { width } = WindowDimension();

  const dispatch = useDispatch();

  const { tiposPlantillas, roles } = useSelector(
    (state) => state.tiposPlantillas
  );

  const { submitAction } = useSubmit();

  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    reset,
    setValue,
  } = useForm();

  const columns = useMemo(() => {
    const templateTypesTable = adminTemplateTypePage?.templatetypes_table ?? [];
    return [
      { label: templateTypesTable[0]?.label || 'Código', accessor: 'tipoPlantillaId', sortable: true },
      { 
        label: templateTypesTable[1]?.label || 'Nombre', 
        accessor: 'tipoPlantillaNombre', 
        sortable: true,
        render: (item) => (
          <td>{translatedNombres[item.tipoPlantillaNombre] || item.tipoPlantillaNombre}</td>
        ),
      },
      {
        label: templateTypesTable[2]?.label || 'Destinatarios',
        accessor: 'roles',
        sortable: true,
        render: (item) => (
          <td>
            {!item.roles 
              ? "-" 
              : translatedDestinatarios[item.roles] || item.roles
            }
          </td>
        ),
      },
      { label: templateTypesTable[3]?.label || 'Acción', accessor: 'action', sortable: false },
    ];
  }, [adminTemplateTypePage, translatedNombres, translatedDestinatarios]);

  const showModalEdit = (reg) => {
    multiSelectRef.current.resetSelectedValues();
    reset(reg);
    selectReg(reg);
    changeModalState(true);
  };

  const closeModalEdit = () => {
    changeModalState(false);
  };

  const cargarPlantillas = () => {
    dispatch(obtenerTiposPlantillas());
  };
  const cargarRoles = () => {
    dispatch(obtenerRolesPlantillas());
  };

  const actualizar = async (data) => {
    submitAction(actualizarTipoPlantilla, data, () => {
      closeModalEdit();
      cargarPlantillas();
    });
  };

  useEffect(() => {
    cargarPlantillas();
    cargarRoles();
  }, [dispatch]);

  useEffect(() => {
    let cancel = false;
    setIsLoadingContent(true);
    getAdminTemplateTypePage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setAdminTemplateTypePage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => { 
        if (!cancel) {
          setAdminTemplateTypePage(null);
          setIsLoadingContent(false);
        }
      });
    return () => { cancel = true };
  }, [locale])

  // Efecto para traducir los campos "Nombre" y "Destinatarios" cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasTiposPlantillas = tiposPlantillas && Array.isArray(tiposPlantillas) && tiposPlantillas.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasTiposPlantillas) {
        setIsTranslating(false);
        return;
      }

      // Iniciar traducción
      setIsTranslating(true);

      const nombreTranslations = {};
      const destinatariosTranslations = {};

      // Traducir solo los campos solicitados
      const translationPromises = tiposPlantillas.map(async (item) => {
        if (item.tipoPlantillaNombre) {
          const translated = await _translateValue(SCREEN_NAME, item.tipoPlantillaNombre, locale);
          nombreTranslations[item.tipoPlantillaNombre] = translated;
        }
        if (item.roles) {
          const translated = await _translateValue(SCREEN_NAME, item.roles, locale);
          destinatariosTranslations[item.roles] = translated;
        }
      });
      await Promise.all(translationPromises);

      // Actualizar estados y finalizar traducción
      setTranslatedNombres(nombreTranslations);
      setTranslatedDestinatarios(destinatariosTranslations);
      setIsTranslating(false);
    };

    translateData();
  }, [tiposPlantillas, locale]);

  if (isLoadingContent || isTranslating) {
    return (
      <main className="main-admin">
        <div className="main-admin-bar">
          <Skeleton.Input style={{ width: 300, height: 30 }} active />
        </div>
        <div className="table-container">
          <Skeleton.Input style={{ width: '100%', height: 400, minWidth: '800px' }} active />
        </div>
      </main>
    );
  }

  return (
    <main className="main-admin">
      <div className="main-admin-bar">
        <h2>{adminTemplateTypePage?.title || "Tipos de plantillas"}</h2>
      </div>

      <Modal
        estado={modalState}
        cambiarEstado={closeModalEdit}
        titulo={adminTemplateTypePage?.subtitle ||"Actualizar tipo de plantilla"}
        clase={`${width < 576 ? "full-width" : "small"}`}
        buttonGroup={
          <>
            <button className="button-text" onClick={handleSubmit(actualizar)}>
              {adminTemplateTypePage?.save_button ||"Guardar"}
            </button>
          </>
        }
      >
        <form>
          <div>
            <div className="form-group">
              <label>{adminTemplateTypePage?.name ||"Nombre"}</label>
              <Required />
              <input
                className="form-control"
                id="titleEdit"
                type="text"
                {...register("tipoPlantillaNombre", {
                  required: validation?.required_field || "Campo requerido",
                })}
                name="tipoPlantillaNombre"
              ></input>
              {errors.tipoPlantillaNombre && (
                <p className="error">{errors.tipoPlantillaNombre.message}</p>
              )}
            </div>
          </div>
          <div className="form-group">
            <label>{adminTemplateTypePage?.Recipient ||"Destinatario"}</label>
            <Required />
            <Multiselect
              innerRef={multiSelectRef}
              options={roles}
              selectedValues={registerSelected.destinatarios}
              name="destinatarios"
              setValueArray="destinatarios"
              displayValue="rolNombre"
              control={control}
              setValue={setValue}
              placeholder="Seleccione los destinatarios"
              rules={{ required: validation?.required_field || "Campo requerido" }}
            />
            {errors.destinatarios && (
              <p className="error">{errors.destinatarios.message}</p>
            )}
          </div>
        </form>
      </Modal>

      <div className="table-container">
        {tiposPlantillas.length > 0 ? (
          <>
            <OrderTable
              {...{ columns }}
              data={tiposPlantillas}
              clase="table-adm"
              actions={[
                {
                  icon: "",
                  clase: "text-primary",
                  event: (item) => showModalEdit(item),
                },
              ]}
            />
          </>
        ) : (
          <div className="no-results-container">
            <img src={props.img} alt="Imagen sin resultados" />
            <h1>{props.noResultMessage}</h1>
          </div>
        )}
      </div>
    </main>
  );
}

export default TiposPlantillas;
